package org.example.project.enginetabular

import org.example.project.utils.All

internal fun creatorPasteResult(
    document: All,
    statusText: String,
    clearHeaderSelection: Boolean = false,
    clearSelectedCell: Boolean = false,
    clearContextMenu: Boolean = false
): All = All(
    module = listOf(document),
    bemerkung = statusText,
    clearHeaderSelection = clearHeaderSelection,
    clearSelectedCell = clearSelectedCell,
    clearContextMenu = clearContextMenu
)

internal fun creatorPasteDocument(result: All): All = result.module.firstOrNull() ?: createEmptyDocument()
internal fun creatorPasteStatus(result: All): String = result.bemerkung
internal fun creatorPasteClearHeaderSelection(result: All): Boolean = result.clearHeaderSelection
internal fun creatorPasteClearSelectedCell(result: All): Boolean = result.clearSelectedCell
internal fun creatorPasteClearContextMenu(result: All): Boolean = result.clearContextMenu

internal fun importCreatorDocumentFromText(
    text: String,
    currentDocument: All,
    tableName: String
): All? {
    val block = parseDelimitedBlock(text)
    if (block.isEmpty()) return null

    val headers = block.first().ifEmpty { listOf("") }
    val document = TabularManager.createEmptyTable(
        name = tableName.ifBlank { currentDocument.name },
        headers = headers
    ).copy(id = currentDocument.id)
    val rows = TabularManager.normalizeRows(
        block.drop(1).map { values ->
            All(columns = List(document.columnSettings.size) { col -> values.getOrNull(col).orEmpty() })
        },
        document.columnSettings.size
    )

    return creatorPasteResult(
        document = ensureRectangular(document.copy(module = rows)),
        statusText = "Datei importiert."
    )
}

internal fun applyCreatorPaste(
    currentDocument: All,
    selectedHeaderCol: Int?,
    selectedCell: Pair<String, Int>?,
    selectedCellRange: TabularCellRange?,
    text: String,
    activeSelectionRange: TabularCellRange?
): All? {
    val block = parseDelimitedBlock(text)
    if (block.isEmpty()) return null

    val normalized = ensureRectangular(currentDocument)
    val target = when {
        selectedHeaderCol != null -> "HEADER"
        selectedCell != null || selectedCellRange != null -> "CELL"
        else -> "HEADER"
    }

    if (target == "HEADER") {
        val startCol = selectedHeaderCol ?: 0
        val prepared = ensureCreatorCapacity(
            document = normalized,
            requiredCols = startCol + (block.maxOfOrNull { it.size } ?: 0),
            requiredRows = maxOf(normalized.module.size, block.drop(1).size)
        )

        val headers = prepared.columnSettings.toMutableList()
        block.first().forEachIndexed { index, value ->
            val col = startCol + index
            if (col in headers.indices) {
                headers[col] = headers[col].copy(label = value)
            }
        }

        return creatorPasteResult(
            document = applyCreatorBlock(
                document = prepared.copy(columnSettings = headers),
                startRow = 0,
                startCol = startCol,
                block = block.drop(1)
            ),
            statusText = "Header und Werte eingefügt.",
            clearSelectedCell = true
        )
    }

    val start = selectedCell ?: selectedCellRange?.let { range ->
        normalized.module.getOrNull(range.topRow)?.id?.let { rowId -> rowId to range.leftCol }
    } ?: return null
    val startRowIndex = normalized.module.indexOfFirst { it.id == start.first }.takeIf { it >= 0 } ?: 0
    val startColIndex = start.second
    val prepared = ensureCreatorCapacity(
        document = normalized,
        requiredCols = startColIndex + (block.maxOfOrNull { it.size } ?: 0),
        requiredRows = startRowIndex + block.size
    )

    if (block.size == 1 &&
        block.first().size == 1 &&
        activeSelectionRange != null &&
        (activeSelectionRange.topRow != activeSelectionRange.bottomRow || activeSelectionRange.leftCol != activeSelectionRange.rightCol)
    ) {
        val fillValue = block.first().firstOrNull().orEmpty()
        val width = activeSelectionRange.rightCol - activeSelectionRange.leftCol + 1
        val height = activeSelectionRange.bottomRow - activeSelectionRange.topRow + 1
        return creatorPasteResult(
            document = applyCreatorBlock(
                document = ensureCreatorCapacity(
                    document = currentDocument,
                    requiredCols = maxOf(activeSelectionRange.rightCol + 1, activeSelectionRange.leftCol + width),
                    requiredRows = maxOf(activeSelectionRange.bottomRow + 1, activeSelectionRange.topRow + height)
                ),
                startRow = activeSelectionRange.topRow,
                startCol = activeSelectionRange.leftCol,
                block = List(height) { List(width) { fillValue } }
            ),
            statusText = "Bereich mit Zwischenablage gefüllt.",
            clearHeaderSelection = true,
            clearContextMenu = true
        )
    }

    return creatorPasteResult(
        document = applyCreatorBlock(prepared, startRowIndex, startColIndex, block),
        statusText = "${block.size} Zeile(n) eingefügt.",
        clearHeaderSelection = true
    )
}
